import { useState, useEffect, useRef } from "react"

interface AiLoadingCardProps {
  /** Rotating context messages shown while AI is working. */
  messages: string[]
  /** Interval between message rotations in ms. Default 2400. */
  interval?: number
  /** Extra CSS class names on the outer wrapper. */
  className?: string
  /** Number of shimmer placeholder lines below the message. Default 2. */
  lines?: number
}

/**
 * Dark intelligence-surface loading card with pulsing AI badge,
 * rotating contextual message, and shimmer placeholder lines.
 *
 * Timing: first message appears instantly, second after ~1.2s,
 * then rotates at `interval` (default 2.4s). Fade is 200ms.
 *
 * Reuses the `ov-*` animation system (shimmer, badge pulse, card enter).
 */
export function AiLoadingCard({
  messages,
  interval = 2400,
  className = "",
  lines = 2,
}: AiLoadingCardProps) {
  const [index, setIndex] = useState(0)
  const [fading, setFading] = useState(false)
  const isFirstRotation = useRef(true)

  useEffect(() => {
    if (messages.length <= 1) return

    // First rotation happens sooner (1.2s) to feel responsive
    const firstDelay = isFirstRotation.current ? 1200 : interval

    const timer = setTimeout(() => {
      setFading(true)
      setTimeout(() => {
        setIndex(i => (i + 1) % messages.length)
        setFading(false)
        isFirstRotation.current = false
      }, 200)
    }, firstDelay)

    return () => clearTimeout(timer)
  }, [index, messages.length, interval])

  return (
    <div className={`ov-loading-card ov-loading-card--intelligence ${className}`.trim()}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span className="ov-ai-badge">
          <span className="ov-ai-badge__dot" />
          AI
        </span>
      </div>
      <div
        className="ov-ai-message"
        style={{ opacity: fading ? 0 : 1 }}
      >
        {messages[index]}
      </div>
      {Array.from({ length: lines }, (_, i) => (
        <div
          key={i}
          className="ov-skeleton--dark ov-skeleton--text"
          style={{
            width: i === 0 ? "60%" : "40%",
            height: 14,
            marginTop: i === 0 ? 12 : 8,
          }}
        />
      ))}
    </div>
  )
}
